// Shared front-end primitives. These were previously copy-pasted across app.js,
// studio.js, album-viewer.js, section-pages.js, journal.js and cv.js, which let
// them drift apart (three different `track` implementations, two identical
// escape helpers inside a single file).

export const escapeHTML = (value = '') => String(value).replace(/[&<>"']/g, (character) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
})[character]);

export const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------------------------------------------------------------- placeholders */

// A neutral brand-grey tile used while real media loads, and as the resting
// state when a slot has no media yet. Rendering it at the correct aspect ratio
// is what stops the page from reflowing once the real image arrives.
export const PLACEHOLDER_RATIO = 3 / 2;

export function placeholderImage(ratio = PLACEHOLDER_RATIO) {
  const safeRatio = Number.isFinite(ratio) && ratio > 0 ? ratio : PLACEHOLDER_RATIO;
  const width = 120;
  const height = Math.max(1, Math.round(width / safeRatio));
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`
    + `<rect width="${width}" height="${height}" fill="#191919"/></svg>`
  )}`;
}

// Media starts as a grey block and only reveals once it has actually decoded.
// On failure it stays grey rather than showing a broken-image glyph.
export function trackMediaLoad(node) {
  if (!node || node.dataset.mediaWatched === '1') return node;
  node.dataset.mediaWatched = '1';
  const frame = node.closest('[data-media-frame]') || node.parentElement;
  const settle = (state) => {
    node.classList.add('is-media-settled');
    node.classList.toggle('is-media-failed', state === 'error');
    if (frame) frame.classList.remove('is-media-loading');
  };
  if (frame) frame.classList.add('is-media-loading');
  const ready = node.tagName === 'VIDEO' ? node.readyState >= 2 : node.complete && node.naturalWidth > 0;
  if (ready) {
    settle('load');
    return node;
  }
  node.addEventListener(node.tagName === 'VIDEO' ? 'loadeddata' : 'load', () => settle('load'), { once: true });
  node.addEventListener('error', () => settle('error'), { once: true });
  return node;
}

export function watchAllMedia(root = document) {
  root.querySelectorAll('img, video').forEach(trackMediaLoad);
}

/* -------------------------------------------------------------------- labelling */

// Camera exports like `40266.png` or `IMG_0042` carry no meaning for a screen
// reader, so they must never become alt text.
export function usefulMediaLabel(name = '') {
  const cleaned = String(name)
    .replace(/\.[a-z0-9]{2,5}$/i, '')
    .replace(/[_-]+/g, ' ')
    .trim();
  if (!/[a-zÀ-ɏ]{2}/i.test(cleaned)) return '';
  if (/^\d+$/.test(cleaned)) return '';
  if (/^(img|dsc|dscf|p|pxl|photo|image|screenshot)\s*\d+$/i.test(cleaned)) return '';
  return cleaned;
}

export function mediaAltText(item = {}, contextTitle = '', index = 0) {
  return item.alt
    || usefulMediaLabel(item.name)
    || `${contextTitle} frame ${String(index + 1).padStart(2, '0')}`.trim();
}

/* -------------------------------------------------------------------- analytics */

export function visitorId() {
  const key = 'nero_visitor_id_v1';
  try {
    const current = localStorage.getItem(key);
    if (current) return current;
    const next = crypto.randomUUID();
    localStorage.setItem(key, next);
    return next;
  } catch (_) {
    return 'anonymous';
  }
}

export function track(name, detail = {}) {
  const metadata = { ...detail, visitorId: visitorId() };
  try {
    const key = 'nero_events_v1';
    const events = JSON.parse(localStorage.getItem(key) || '[]');
    events.push({ name, detail: metadata, path: location.pathname, at: new Date().toISOString() });
    localStorage.setItem(key, JSON.stringify(events.slice(-500)));
  } catch (_) { /* Private browsing or a full quota must not break tracking. */ }
  fetch('/api/events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, path: location.pathname, metadata }),
    keepalive: true
  }).catch(() => {});
}

export function debounce(fn, wait = 400) {
  let timer = 0;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

/* ---------------------------------------------------------------------- runtime */

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  const register = () => navigator.serviceWorker.register('/service-worker.js').catch(() => {});
  // Module scripts are deferred, so `load` may already have fired by the time
  // this runs. Waiting on it unconditionally silently skipped registration.
  if (document.readyState === 'complete') register();
  else addEventListener('load', register, { once: true });
}

// Builds a `mailto:` the visitor's own mail client sends. Used instead of
// pretending a form was delivered when there is no mail provider wired up.
export function mailtoLink(address, subject, body) {
  const target = address || 'contact@nerosoares.com';
  const query = new URLSearchParams();
  if (subject) query.set('subject', subject);
  if (body) query.set('body', body);
  const search = query.toString().replace(/\+/g, '%20');
  return `mailto:${target}${search ? `?${search}` : ''}`;
}
